/**
 * Freshness — how recently the number you are looking at was actually true.
 *
 * WHY THIS EXISTS
 * This dashboard is an instrument, and the operator's real question is not
 * "what is the battery" but "is what I am reading true RIGHT NOW". Until now
 * nothing on screen answered that. A value polled 11 seconds ago and a value
 * from a device that stopped answering two minutes ago rendered identically,
 * which is how a stale dashboard reads as a live one.
 *
 * So freshness is a first-class citizen rather than a tooltip: it sits next to
 * the values it qualifies, and it is driven by the same link machine that owns
 * the polling. That coupling is the point -- there is exactly one notion of
 * "current" in the app, and the UI cannot disagree with the transport about it.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { t } from "i18n";

/* Thresholds are tied to the poll cadences in useDeviceLink, not picked for
 * looks: IDLE_POLL_MS is 8 s, so anything under ~10 s is a normal beat and
 * must read as live or the indicator cries wolf on a healthy device. Past
 * ~25 s we have missed several polls and the operator deserves to know. */
const LIVE_MS = 10000;
const AGING_MS = 25000;

export const FRESHNESS = {
  LIVE: "live",
  AGING: "aging",
  STALE: "stale",
  UNKNOWN: "unknown",
};

export function freshnessOf(ageMs) {
  if (ageMs == null || !Number.isFinite(ageMs)) return FRESHNESS.UNKNOWN;
  if (ageMs < LIVE_MS) return FRESHNESS.LIVE;
  if (ageMs < AGING_MS) return FRESHNESS.AGING;
  return FRESHNESS.STALE;
}

export function freshnessLabel(ageMs) {
  if (ageMs == null || !Number.isFinite(ageMs))
    return t("analysis.freshness.noReading");
  const s = Math.round(ageMs / 1000);
  if (s < 2) return t("analysis.freshness.justNow");
  if (s < 60) return t("analysis.freshness.secondsAgo", { count: s });
  const m = Math.floor(s / 60);
  return t("analysis.freshness.minutesAgo", { count: m });
}

const TONE = {
  [FRESHNESS.LIVE]: { dot: "#15803D", text: "#9CA3AF" },
  [FRESHNESS.AGING]: { dot: "#B45309", text: "#B45309" },
  [FRESHNESS.STALE]: { dot: "#DC2626", text: "#DC2626" },
  [FRESHNESS.UNKNOWN]: { dot: "#C4C7CD", text: "#C4C7CD" },
};

/**
 * Ticks on its own so the age keeps counting up even when no data arrives --
 * which is exactly the case that matters. A component that only re-rendered on
 * new data would freeze at "2s ago" forever on a dead link, showing the
 * opposite of the truth.
 *
 * @param {number} at  performance.now() stamp of the last reading, 0 if none
 * @param {boolean} showLabel  render the age in words beside the dot
 * @param {boolean} showDot  render the coloured dot (off when the caller
 *   already leads with its own status dot)
 */
export const Freshness = ({
  at,
  showLabel = true,
  showDot = true,
  className = "",
}) => {
  const { t } = useTranslation();
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const age = at ? performance.now() - at : null;
  const level = freshnessOf(age);
  const tone = TONE[level];
  const label = freshnessLabel(age);

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${className}`}
      title={t("analysis.freshness.titleLastReading", { label })}
    >
      {showDot && (
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          {level === FRESHNESS.LIVE && (
            <span
              className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
              style={{ backgroundColor: tone.dot }}
            />
          )}
          <span
            className="relative inline-flex h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: tone.dot }}
          />
        </span>
      )}
      {showLabel && (
        <span className="text-[11px] tabular-nums" style={{ color: tone.text }}>
          {label}
        </span>
      )}
      <span className="sr-only">
        {t("analysis.freshness.srLastReading", { label })}
      </span>
    </span>
  );
};

export default Freshness;
